/*!
 * vue-bem-styling v0.10.21 
 * (c) 2023 Alex Tkachenko
 * Released under the MIT License.
 */
import { createBlockGenerator, createStateGenerator } from '@aspectus/bem';

var BLOCK_MODIFIERS = {
  theme: 'theme',
  view: 'view',
  styling: 'styling',
  variant: 'variant',
  appearance: 'appearance',
  kind: 'kind',
  align: 'align',
  fit: 'fit'
};
var BLOCK_STATES = {
  disabled: 'disabled',
  passive: 'passive',
  readonly: 'readonly',
  required: 'required',
  invalid: 'invalid',
  active: 'active',
  opened: 'opened',
  hidden: 'hidden'
};

function _typeof(obj) {
  if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") {
    _typeof = function (obj) {
      return typeof obj;
    };
  } else {
    _typeof = function (obj) {
      return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj;
    };
  }

  return _typeof(obj);
}

function _isPlaceholder(a) {
  return a != null && _typeof(a) === 'object' && a['@@functional/placeholder'] === true;
}

var _isPlaceholder_1 = _isPlaceholder;

/**
 * Optimized internal one-arity curry function.
 *
 * @private
 * @category Function
 * @param {Function} fn The function to curry.
 * @return {Function} The curried function.
 */

function _curry1(fn) {
  return function f1(a) {
    if (arguments.length === 0 || _isPlaceholder_1(a)) {
      return f1;
    } else {
      return fn.apply(this, arguments);
    }
  };
}

var _curry1_1 = _curry1;

function _arity(n, fn) {
  /* eslint-disable no-unused-vars */
  switch (n) {
    case 0:
      return function () {
        return fn.apply(this, arguments);
      };

    case 1:
      return function (a0) {
        return fn.apply(this, arguments);
      };

    case 2:
      return function (a0, a1) {
        return fn.apply(this, arguments);
      };

    case 3:
      return function (a0, a1, a2) {
        return fn.apply(this, arguments);
      };

    case 4:
      return function (a0, a1, a2, a3) {
        return fn.apply(this, arguments);
      };

    case 5:
      return function (a0, a1, a2, a3, a4) {
        return fn.apply(this, arguments);
      };

    case 6:
      return function (a0, a1, a2, a3, a4, a5) {
        return fn.apply(this, arguments);
      };

    case 7:
      return function (a0, a1, a2, a3, a4, a5, a6) {
        return fn.apply(this, arguments);
      };

    case 8:
      return function (a0, a1, a2, a3, a4, a5, a6, a7) {
        return fn.apply(this, arguments);
      };

    case 9:
      return function (a0, a1, a2, a3, a4, a5, a6, a7, a8) {
        return fn.apply(this, arguments);
      };

    case 10:
      return function (a0, a1, a2, a3, a4, a5, a6, a7, a8, a9) {
        return fn.apply(this, arguments);
      };

    default:
      throw new Error('First argument to _arity must be a non-negative integer no greater than ten');
  }
}

var _arity_1 = _arity;

/**
 * Optimized internal two-arity curry function.
 *
 * @private
 * @category Function
 * @param {Function} fn The function to curry.
 * @return {Function} The curried function.
 */

function _curry2(fn) {
  return function f2(a, b) {
    switch (arguments.length) {
      case 0:
        return f2;

      case 1:
        return _isPlaceholder_1(a) ? f2 : _curry1_1(function (_b) {
          return fn(a, _b);
        });

      default:
        return _isPlaceholder_1(a) && _isPlaceholder_1(b) ? f2 : _isPlaceholder_1(a) ? _curry1_1(function (_a) {
          return fn(_a, b);
        }) : _isPlaceholder_1(b) ? _curry1_1(function (_b) {
          return fn(a, _b);
        }) : fn(a, b);
    }
  };
}

var _curry2_1 = _curry2;

/**
 * Internal curryN function.
 *
 * @private
 * @category Function
 * @param {Number} length The arity of the curried function.
 * @param {Array} received An array of arguments received thus far.
 * @param {Function} fn The function to curry.
 * @return {Function} The curried function.
 */

function _curryN(length, received, fn) {
  return function () {
    var combined = [];
    var argsIdx = 0;
    var left = length;
    var combinedIdx = 0;

    while (combinedIdx < received.length || argsIdx < arguments.length) {
      var result;

      if (combinedIdx < received.length && (!_isPlaceholder_1(received[combinedIdx]) || argsIdx >= arguments.length)) {
        result = received[combinedIdx];
      } else {
        result = arguments[argsIdx];
        argsIdx += 1;
      }

      combined[combinedIdx] = result;

      if (!_isPlaceholder_1(result)) {
        left -= 1;
      }

      combinedIdx += 1;
    }

    return left <= 0 ? fn.apply(this, combined) : _arity_1(left, _curryN(length, combined, fn));
  };
}

var _curryN_1 = _curryN;

/**
 * Returns a curried equivalent of the provided function, with the specified
 * arity. The curried function has two unusual capabilities. First, its
 * arguments needn't be provided one at a time. If `g` is `R.curryN(3, f)`, the
 * following are equivalent:
 *
 *   - `g(1)(2)(3)`
 *   - `g(1)(2, 3)`
 *   - `g(1, 2)(3)`
 *   - `g(1, 2, 3)`
 *
 * Secondly, the special placeholder value [`R.__`](#__) may be used to specify
 * "gaps", allowing partial application of any combination of arguments,
 * regardless of their positions. If `g` is as above and `_` is [`R.__`](#__),
 * the following are equivalent:
 *
 *   - `g(1, 2, 3)`
 *   - `g(_, 2, 3)(1)`
 *   - `g(_, _, 3)(1)(2)`
 *   - `g(_, _, 3)(1, 2)`
 *   - `g(_, 2)(1)(3)`
 *   - `g(_, 2)(1, 3)`
 *   - `g(_, 2)(_, 3)(1)`
 *
 * @func
 * @memberOf R
 * @since v0.5.0
 * @category Function
 * @sig Number -> (* -> a) -> (* -> a)
 * @param {Number} length The arity for the returned function.
 * @param {Function} fn The function to curry.
 * @return {Function} A new, curried function.
 * @see R.curry
 * @example
 *
 *      const sumArgs = (...args) => R.sum(args);
 *
 *      const curriedAddFourNumbers = R.curryN(4, sumArgs);
 *      const f = curriedAddFourNumbers(1, 2);
 *      const g = f(3);
 *      g(4); //=> 10
 */

var curryN =
/*#__PURE__*/
_curry2_1(function curryN(length, fn) {
  if (length === 1) {
    return _curry1_1(fn);
  }

  return _arity_1(length, _curryN_1(length, [], fn));
});

var curryN_1 = curryN;

/**
 * Returns a curried equivalent of the provided function. The curried function
 * has two unusual capabilities. First, its arguments needn't be provided one
 * at a time. If `f` is a ternary function and `g` is `R.curry(f)`, the
 * following are equivalent:
 *
 *   - `g(1)(2)(3)`
 *   - `g(1)(2, 3)`
 *   - `g(1, 2)(3)`
 *   - `g(1, 2, 3)`
 *
 * Secondly, the special placeholder value [`R.__`](#__) may be used to specify
 * "gaps", allowing partial application of any combination of arguments,
 * regardless of their positions. If `g` is as above and `_` is [`R.__`](#__),
 * the following are equivalent:
 *
 *   - `g(1, 2, 3)`
 *   - `g(_, 2, 3)(1)`
 *   - `g(_, _, 3)(1)(2)`
 *   - `g(_, _, 3)(1, 2)`
 *   - `g(_, 2)(1)(3)`
 *   - `g(_, 2)(1, 3)`
 *   - `g(_, 2)(_, 3)(1)`
 *
 * @func
 * @memberOf R
 * @since v0.1.0
 * @category Function
 * @sig (* -> a) -> (* -> a)
 * @param {Function} fn The function to curry.
 * @return {Function} A new, curried function.
 * @see R.curryN, R.partial
 * @example
 *
 *      const addFourNumbers = (a, b, c, d) => a + b + c + d;
 *
 *      const curriedAddFourNumbers = R.curry(addFourNumbers);
 *      const f = curriedAddFourNumbers(1, 2);
 *      const g = f(3);
 *      g(4); //=> 10
 */

var curry =
/*#__PURE__*/
_curry1_1(function curry(fn) {
  return curryN_1(fn.length, fn);
});

var curry_1 = curry;

var mapSame = function mapSame() {
  for (var _len = arguments.length, keys = new Array(_len), _key = 0; _key < _len; _key++) {
    keys[_key] = arguments[_key];
  }

  return keys.reduce(function (acc, x) {
    acc[x] = x;
    return acc;
  }, {});
};
var extractProps = curry_1(function (propsMap, data) {
  return Object.keys(propsMap).reduce(function (acc, x) {
    acc[propsMap[x]] = data[x];
    return acc;
  }, {});
});
var extractChangers = function extractChangers(props) {
  var modifiers = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : BLOCK_MODIFIERS;
  var states = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : BLOCK_STATES;
  return [extractProps(modifiers, props), extractProps(states, props)];
};

/* eslint-disable prefer-object-spread */
var MODIFIER_PROP_DEFINITION = {
  type: [String, Array, Boolean, Number]
};
var STATE_PROP_DEFINITION = {
  type: Boolean,
  default: false
};

var samePropReducer = function samePropReducer(prop) {
  return function (acc, x) {
    acc[x] = prop;
    return acc;
  };
};

var constructProps = function constructProps(map, definition) {
  return Object.keys(map).reduce(samePropReducer(definition), {});
};
var MODIFIER_PROPS = constructProps(BLOCK_MODIFIERS, MODIFIER_PROP_DEFINITION);
var STATE_PROPS = constructProps(BLOCK_STATES, STATE_PROP_DEFINITION);
var STYLING_PROPS = Object.assign({}, MODIFIER_PROPS, STATE_PROPS);
var StylingPropsMixin = {
  props: STYLING_PROPS
};
var createStylingFunctionalMixin = function createStylingFunctionalMixin() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref$blockConfig = _ref.blockConfig,
      blockConfig = _ref$blockConfig === void 0 ? {} : _ref$blockConfig,
      _ref$stateConfig = _ref.stateConfig,
      stateConfig = _ref$stateConfig === void 0 ? {} : _ref$stateConfig,
      _ref$modifiers = _ref.modifiers,
      modifiers = _ref$modifiers === void 0 ? BLOCK_MODIFIERS : _ref$modifiers,
      _ref$states = _ref.states,
      states = _ref$states === void 0 ? BLOCK_STATES : _ref$states;

  return {
    mixins: [StylingPropsMixin],
    props: {
      blockName: String,
      // Block class generator
      bcg: {
        type: Function,
        default: createBlockGenerator(blockConfig)
      },
      // Block state generator
      bsg: {
        type: Function,
        default: createStateGenerator(stateConfig)
      },
      bclass: {
        type: Function,
        default: function _default() {
          var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : this;
          return [props.bcg(props.blockName)(extractProps(modifiers)(props)), props.bsg(extractProps(states)(props))];
        }
      },
      eclass: {
        type: Function,
        default: function _default(element) {
          var md = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
          var props = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : this;
          return props.bcg(props.blockName)(element, md);
        }
      }
    }
  };
};
var StylingFunctionalMixin = createStylingFunctionalMixin();
var StylingMixin = {
  mixins: [StylingPropsMixin],
  BLOCK_MODIFIERS: BLOCK_MODIFIERS,
  BLOCK_STATES: BLOCK_STATES,
  computed: {
    modifiers: function modifiers() {
      return this.extractModifiers();
    },
    states: function states() {
      return this.extractStates();
    }
  },
  methods: {
    extractModifiers: function extractModifiers() {
      var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : this;
      return extractProps(this.$options.BLOCK_MODIFIERS)(props);
    },
    extractStates: function extractStates() {
      var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : this;
      return extractProps(this.$options.BLOCK_STATES)(props);
    }
  }
};

export { BLOCK_MODIFIERS, BLOCK_STATES, MODIFIER_PROPS, MODIFIER_PROP_DEFINITION, STATE_PROPS, STATE_PROP_DEFINITION, STYLING_PROPS, StylingFunctionalMixin, StylingMixin, StylingPropsMixin, constructProps, createStylingFunctionalMixin, extractChangers, extractProps, mapSame };
